import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { getHeaders } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface UsersTabProps {
  propertyId: string;
}

interface User {
  UID: string;
  Admin: boolean;
  EmailAddress: string;
  FirstName: string;
  LastName: string;
}

const UsersTab: React.FC<UsersTabProps> = ({ propertyId }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [propertyUserUIDs, setPropertyUserUIDs] = useState<string[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch users for the current company
  // Fetch company users (single known endpoint)
  const fetchCompanyUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const headers = await getHeaders();
      const response = await fetch(`${import.meta.env.VITE_API_URL}/companyUsers`, {
        method: "GET",
        headers,
      });
      if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
      const data: User[] = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error",
        description: "Failed to fetch users. Please try again later.",
        variant: "destructive",
      });
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchPropertyUsers = async () => {
    if (!propertyId) return;
    try {
      const headers = await getHeaders();
      const resp = await fetch(`${import.meta.env.VITE_API_URL}/getPropertyUsers?propertyId=${propertyId}`, {
        method: "GET",
        headers,
      });
      if (!resp.ok) throw new Error(`Failed to fetch property users: ${resp.status}`);
      const data: any[] = await resp.json();
      // expect array of users or objects containing UID
      const uids = (data || []).map((u) => {
        if (!u) return null;
        if (typeof u === "string") return u;
        if (typeof u === "object") return u.UID || u.uid || u.userId || u.id || null;
        return null;
      }).filter(Boolean) as string[];
      setPropertyUserUIDs(uids);
    } catch (err) {
      console.error("Error fetching property users", err);
    }
  };

  const grantPropertyAccess = async (userId: string) => {
    try {
      const headers = await getHeaders();
      const resp = await fetch(`${import.meta.env.VITE_API_URL}/propertyUsers`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ propertyId, userId }),
      });

      if (!resp.ok) throw new Error(`Failed to grant access: ${resp.status}`);
      setPropertyUserUIDs((prev) => Array.from(new Set([...prev, userId])));
      toast({ title: "Access granted", description: "User now has access to this property." });
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Failed to grant access.", variant: "destructive" });
    }
  };

  const revokePropertyAccess = async (userId: string) => {
    try {
      const headers = await getHeaders();
      const resp = await fetch(`${import.meta.env.VITE_API_URL}/propertyUsers`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ propertyId, userId }),
      });
      if (!resp.ok) throw new Error(`Failed to revoke access: ${resp.status}`);
      setPropertyUserUIDs((prev) => prev.filter((u) => u !== userId));
      toast({ title: "Access revoked", description: "User access removed from this property." });
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Failed to revoke access.", variant: "destructive" });
    }
  };

  useEffect(() => {
    // load property users first (to know which have access), then load all users
    const load = async () => {
      await fetchPropertyUsers();
      await fetchCompanyUsers();
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const headers = await getHeaders(); // Retrieve headers using getHeaders()

      const payload = {
        email,
        firstName,
        lastName,
        password: temporaryPassword,
        admin: isAdmin,
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL}/user/invite`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json", // Ensure the content type is JSON
        },
        body: JSON.stringify(payload), // Send the payload as a JSON string
      });

      if (response.status !== 200) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to invite user");
      }

      toast({
        title: "User Added",
        description: `The account for ${firstName} ${lastName} has been created successfully.`,
      });

      // Reset form fields
      setFirstName("");
      setLastName("");
      setEmail("");
      setTemporaryPassword("");
      setIsAdmin(false);
    } catch (error) {
      console.error("Error inviting user:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to invite user. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Users Card */}
      <Card>
        <CardHeader>
          <CardTitle>Current Users</CardTitle>
          <CardDescription>
            Below is the list of users associated with this property.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingUsers ? (
            <div className="flex justify-center items-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : users.length > 0 ? (
            <div className="space-y-4">
              {users.map((user) => {
                const hasAccess = propertyUserUIDs.includes(user.UID);
                return (
                  <div key={user.UID} className="flex justify-between items-center border-b pb-2">
                    <div>
                      <p className="font-medium">{user.FirstName} {user.LastName}</p>
                      <p className="text-sm text-muted-foreground">{user.EmailAddress}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <p className={`text-sm font-medium ${user.Admin ? "text-primary" : "text-muted-foreground"}`}>
                        {user.Admin ?  "Admin" : "User"}
                      </p>
                      <label className="flex items-center space-x-2">
                        <span className="text-sm">Enabled</span>
                        <input
                          type="checkbox"
                          checked={hasAccess}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) grantPropertyAccess(user.UID);
                            else revokePropertyAccess(user.UID);
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        
                      </label>


                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No users found for this property.
            </p>
          )}
        </CardContent>
      </Card>

      {/* User Invitation Form */}
      <Card>
        <CardHeader>
          <CardTitle>Invite User</CardTitle>
          <CardDescription>
            Fill out the form below to invite a new user to this property.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first-name">First Name</Label>
                <Input
                  id="first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">Last Name</Label>
                <Input
                  id="last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={email}
                autoComplete="off"
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="john.doe@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="temporary-password">Temporary Password</Label>
              <Input
                id="temporary-password"
                type="password"
                value={temporaryPassword}
                onChange={(e) => setTemporaryPassword(e.target.value)}
                required
                placeholder="Temporary Password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="is-admin">Admin</Label>
              <div className="flex items-center space-x-2">
                <input
                  id="is-admin"
                  type="checkbox"
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="is-admin" className="text-sm">
                  Grant admin privileges
                </Label>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Inviting..." : "Invite User"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersTab;
